import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Trash2, RefreshCw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { classifyErr } from "@/lib/admin";

export default function UserVisibility() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [target, setTarget] = useState(null);
  const [zones, setZones] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [override, setOverride] = useState({
    extra_user_ids: [],
    extra_zone_ids: [],
    excluded_user_ids: [],
    excluded_zone_ids: [],
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchExtra, setSearchExtra] = useState("");
  const [searchExclude, setSearchExclude] = useState("");

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const [t, z, u, ov] = await Promise.all([
          api.get(`/users/${userId}`),
          api.get("/zones"),
          api.get("/users", { params: { page: 1, page_size: 100 } }),
          api.get(`/users/${userId}/visibility-override`),
        ]);
        setTarget(t.data);
        setZones(z.data.items);
        setAllUsers(u.data.items);
        if (ov.data) setOverride({
          extra_user_ids: ov.data.extra_user_ids || [],
          extra_zone_ids: ov.data.extra_zone_ids || [],
          excluded_user_ids: ov.data.excluded_user_ids || [],
          excluded_zone_ids: ov.data.excluded_zone_ids || [],
        });
        await refreshPreview();
      } catch (e) {
        toast.error(classifyErr(e));
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const refreshPreview = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/visibility-preview`);
      setPreview(data);
    } catch (e) {
      toast.error(classifyErr(e));
    }
  };

  const userById = useMemo(() => Object.fromEntries(allUsers.map((u) => [u.id, u])), [allUsers]);

  const toggle = (key, id) => {
    setOverride((o) => {
      const arr = o[key];
      const next = arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
      return { ...o, [key]: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${userId}/visibility-override`, override);
      toast.success("Visibility override saved");
      await refreshPreview();
    } catch (e) {
      toast.error(classifyErr(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      try { await api.delete(`/users/${userId}/visibility-override`); } catch (_e) { /* may be 404 */ }
      setOverride({ extra_user_ids: [], extra_zone_ids: [], excluded_user_ids: [], excluded_zone_ids: [] });
      toast.success("Override cleared");
      await refreshPreview();
    } catch (e) {
      toast.error(classifyErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (busy || !target) {
    return (
      <div className="py-20 flex justify-center text-neutral-500 gap-2 items-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const otherUsers = allUsers.filter((u) => u.id !== target.id);
  const filteredExtra = otherUsers.filter(
    (u) =>
      !searchExtra ||
      u.name.toLowerCase().includes(searchExtra.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(searchExtra.toLowerCase())
  );
  const filteredExclude = otherUsers.filter(
    (u) =>
      !searchExclude ||
      u.name.toLowerCase().includes(searchExclude.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(searchExclude.toLowerCase())
  );

  const zoneChip = (id) => zones.find((z) => z.id === id)?.code || id.slice(0, 4);

  return (
    <div className="space-y-6" data-testid="admin-user-visibility">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate("/admin/users")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-neutral-400">Admin · Visibility</div>
          <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900">
            Manage visibility for <span className="tulsi-text-gradient">{target.name}</span>
          </h1>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400">Role</div>
            <div className="font-semibold">{target.role_code}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400">Reporting mgr</div>
            <div className="font-semibold">
              {target.reporting_manager_id ? (userById[target.reporting_manager_id]?.name || target.reporting_manager_id.slice(0, 8)) : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400">Base zones</div>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {(target.assigned_zone_ids || []).map((zid) => (
                <span key={zid} className="text-[11px] px-1.5 py-0.5 rounded bg-[#E5197F]/10 text-[#E5197F]">
                  {zoneChip(zid)}
                </span>
              ))}
              {!target.assigned_zone_ids?.length && <span className="text-xs text-neutral-400">—</span>}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-400">Status</div>
            <Badge className={target.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-700"}>
              {target.status}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Extra */}
        <Card className="border-neutral-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
              + Additional access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Extra zones</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {zones.map((z) => (
                  <label
                    key={z.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                      override.extra_zone_ids.includes(z.id) ? "border-emerald-500 bg-emerald-50" : "border-neutral-200"
                    }`}
                    data-testid={`extra-zone-${z.code}`}
                  >
                    <Checkbox checked={override.extra_zone_ids.includes(z.id)} onCheckedChange={() => toggle("extra_zone_ids", z.id)} />
                    <span>{z.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Extra users</Label>
              <Input value={searchExtra} onChange={(e) => setSearchExtra(e.target.value)} placeholder="Search users…" className="mt-1 h-9" />
              <div className="mt-2 max-h-56 overflow-y-auto border border-neutral-200 rounded-lg divide-y">
                {filteredExtra.map((u) => (
                  <label
                    key={u.id}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${override.extra_user_ids.includes(u.id) ? "bg-emerald-50" : "hover:bg-neutral-50"}`}
                    data-testid={`extra-user-${u.id}`}
                  >
                    <Checkbox checked={override.extra_user_ids.includes(u.id)} onCheckedChange={() => toggle("extra_user_ids", u.id)} />
                    <div className="flex-1">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-[11px] text-neutral-500">{u.email || u.phone} · {u.role_code}</div>
                    </div>
                  </label>
                ))}
                {!filteredExtra.length && <div className="text-xs text-neutral-400 p-3">No users match.</div>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Excluded */}
        <Card className="border-neutral-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-red-600">
              − Restricted access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Excluded zones</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {zones.map((z) => (
                  <label
                    key={z.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                      override.excluded_zone_ids.includes(z.id) ? "border-red-400 bg-red-50" : "border-neutral-200"
                    }`}
                    data-testid={`excl-zone-${z.code}`}
                  >
                    <Checkbox checked={override.excluded_zone_ids.includes(z.id)} onCheckedChange={() => toggle("excluded_zone_ids", z.id)} />
                    <span>{z.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Excluded users</Label>
              <Input value={searchExclude} onChange={(e) => setSearchExclude(e.target.value)} placeholder="Search users…" className="mt-1 h-9" />
              <div className="mt-2 max-h-56 overflow-y-auto border border-neutral-200 rounded-lg divide-y">
                {filteredExclude.map((u) => (
                  <label
                    key={u.id}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${override.excluded_user_ids.includes(u.id) ? "bg-red-50" : "hover:bg-neutral-50"}`}
                  >
                    <Checkbox checked={override.excluded_user_ids.includes(u.id)} onCheckedChange={() => toggle("excluded_user_ids", u.id)} />
                    <div className="flex-1">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-[11px] text-neutral-500">{u.email || u.phone} · {u.role_code}</div>
                    </div>
                  </label>
                ))}
                {!filteredExclude.length && <div className="text-xs text-neutral-400 p-3">No users match.</div>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="border-neutral-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-[#6D2C91]">
              <Eye className="h-4 w-4" /> Effective visibility (preview)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-xl bg-[#6D2C91]/5 border border-[#6D2C91]/20 p-4">
              <div className="text-xs uppercase tracking-widest text-[#6D2C91]/80 mb-1">Users visible</div>
              <div className="text-3xl font-extrabold text-[#6D2C91]" data-testid="viz-preview-count">
                {preview?.is_global ? "All" : preview?.resolved_count ?? "…"}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-400 mb-1">Zones</div>
              <div className="flex flex-wrap gap-1">
                {(preview?.zone_ids || []).map((zid) => (
                  <span key={zid} className="text-[11px] px-1.5 py-0.5 rounded bg-[#E5197F]/10 text-[#E5197F]">
                    {zoneChip(zid)}
                  </span>
                ))}
                {preview?.is_global && <span className="text-xs text-neutral-500">All zones</span>}
                {!preview?.is_global && !preview?.zone_ids?.length && <span className="text-xs text-neutral-400">—</span>}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={refreshPreview} data-testid="viz-refresh">
                <RefreshCw className="h-3 w-3 mr-2" /> Refresh
              </Button>
            </div>
            <div className="text-[11px] text-neutral-500">
              Preview reflects the resolver output <em>after</em> your unsaved changes are saved.
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={save}
          disabled={saving}
          className="tulsi-gradient text-white font-semibold h-10"
          data-testid="viz-save"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save override
        </Button>
        <Button
          variant="outline"
          onClick={reset}
          disabled={saving}
          data-testid="viz-reset"
          className="text-red-600 border-red-200 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Reset to default
        </Button>
      </div>
    </div>
  );
}
